import { useContext } from "react";

import { TenantContext } from "@nens/ranalib";
import { getWmsRequest } from "@nens/react-map-gl/utils";

import { RanaMapFullscreen } from "@/lib/rana-map";

import { getFiles, getLayersOfType } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { CompleteFile } from "../../types/file-reference-with-file";
import { Layer } from "../../types/publication";

interface PublicationMapRasterLayersProps {
  getBeforeId: (layer: Layer) => string;
}

export function PublicationMapScenarioLayers({ getBeforeId }: PublicationMapRasterLayersProps) {
  const layers = usePublicationStore(getLayersOfType("scenario"));
  const files = usePublicationStore(getFiles);

  return (
    <>
      {layers.map((layer) => {
        const file = files.find((file) => file.path === layer.filePath);

        if (!file) return null;

        return (
          <SingleScenarioLayer
            key={layer.id}
            beforeId={getBeforeId(layer)}
            layer={layer}
            file={file}
          />
        );
      })}
    </>
  );
}

interface SingleScenarioLayerProps {
  file: CompleteFile;
  layer: Layer;
  beforeId: string;
}

function SingleScenarioLayer({ file, layer, beforeId }: SingleScenarioLayerProps) {
  const { tenant } = useContext(TenantContext);

  if (layer.layerInFile === null) return null;

  if (!file.file.descriptor.meta || !("envelope" in file.file.descriptor.meta)) {
    return null;
  }

  // Use layerInFile as WMS layer
  const wmsLayer = layer.layerInFile;
  // Get bounds from the file descriptor meta
  const bounds = file.file.descriptor.meta.envelope as [number, number, number, number];

  // Use the file descriptor's WMS endpoint with the layerInFile as layer to get the tiles URL
  const wmsUrl = `/v1-alpha/tenants/${tenant}/file-descriptors/${file.file.descriptorId}/wms`;
  const styles = undefined; // Publications don't support styling for scenario layers, so we don't pass any styles parameter to the WMS request

  const wmsTiles = getWmsRequest(wmsUrl, {
    layer: wmsLayer,
    styles,
    transparent: false,
  });

  return (
    <RanaMapFullscreen.Source bounds={bounds} type="raster" tiles={[wmsTiles]}>
      <RanaMapFullscreen.Layer
        id={`publication-scenario-layer-${layer.id}`}
        beforeId={beforeId}
        type="raster"
        paint={{ "raster-opacity": layer.opacity }}
        isVisible={layer.visible}
      />
    </RanaMapFullscreen.Source>
  );
}
